import type { RouteAttr, Vehicle } from "@/types/fleet";
import type { InputListEntry } from "@/lib/telemetry/attrs";
import { apiRequest } from "@/lib/api/http";
import { logCrud } from "@/lib/activity/emit-store";
import { formatDriverRef, parseDriverRef, vehicleDriverRef } from "@/lib/fleet/driver-ref";

export interface RemoteVehicle {
  id: string;
  device_id?: string;
  imei?: string;
  device_type_id?: string;
  client_group_id?: string;
  maker?: string;
  model?: string;
  registration?: string;
  odometer?: string;
  year?: string;
  last_update?: string;
  x?: string;
  y?: string;
  speed?: string;
  heading?: string;
  alt?: string;
  sats?: string;
  is_active?: string;
  status?: string;
  /** Tagged driver reference: `id:{driverId}` or `c:{code}`; empty string means unassigned. */
  driver_id?: string;
  kapacitet_rezervoara?: string | number;
  /** Vehicle speed limit in km/h. */
  max_speed?: string | number;
  /** Truthy flag — check speed against the vehicle's own max speed. */
  track_speeding?: string | number;
  /** Truthy flag — check speed against the street speed limit. */
  track_street_speeding?: string | number;
  /** From upstream; `/api/vehicles` replaces with `processAttrs` (`RouteAttr`). */
  attrs?: RouteAttr[];
  /** Full device input definitions from ClientDevice (when provided). */
  device_inputs?: InputListEntry[];
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

interface SingleResponse<T> {
  success: true;
  /** `ClientDevice.php` answers a successful PUT with `{ success: true }` and no body. */
  data?: T;
}

function parseNumber(value?: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Mirrors the server-side `parseTankCapacity`: only a positive number counts. */
function parsePositiveNumber(value?: string | number): number | null {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(typeof value === "string" ? value.trim() : value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Upstream is inconsistent about boolean columns: `ClientDevice.php` returns them as JSON ints
 * (`1` / `0`) while the socket payloads and this app's own writes use `"1"` / `"0"` strings.
 */
function parseFlag(value?: string | number | boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  return value === "1";
}

/** Telemetry older than this means the device stopped reporting, not that the vehicle is parked. */
const OFFLINE_AFTER_MS = 60 * 60 * 1000;

function isReporting(vehicle: RemoteVehicle, nowMs: number) {
  if (vehicle.is_active === "0") return false;
  const lastUpdateMs = vehicle.last_update ? Date.parse(vehicle.last_update) : NaN;
  // Upstream omits `last_update` on some endpoints; absence is not evidence of staleness.
  if (!Number.isFinite(lastUpdateMs)) return true;
  return nowMs - lastUpdateMs < OFFLINE_AFTER_MS;
}

/** `status` is the ignition flag, not connectivity — engine off is `stopped`, not `offline`. */
function normalizeStatus(vehicle: RemoteVehicle): Vehicle["status"] {
  if (!isReporting(vehicle, Date.now())) return "offline";
  if (parseNumber(vehicle.speed) > 0) return "moving";
  return vehicle.status === "1" ? "idle" : "stopped";
}

export function mapVehicle(vehicle: RemoteVehicle): Vehicle {
  const maker = vehicle.maker?.trim() ?? "";
  const model = vehicle.model?.trim() ?? "";
  const registration = vehicle.registration?.trim() ?? "";
  const label = [maker, model].filter(Boolean).join(" ").trim();
  const driverRef = parseDriverRef(vehicle.driver_id);

  return {
    id: vehicle.id,
    deviceId: vehicle.device_id?.trim() || undefined,
    deviceTypeId: vehicle.device_type_id?.trim() || undefined,
    name: label || registration || `Vehicle ${vehicle.id}`,
    maker: maker || undefined,
    model: model || undefined,
    year: vehicle.year ? parseNumber(vehicle.year) : null,
    plateNumber: registration || "-",
    groupId: vehicle.client_group_id ?? "",
    groupName: vehicle.client_group_id ?? "-",
    status: normalizeStatus(vehicle),
    speed: parseNumber(vehicle.speed),
    heading: parseNumber(vehicle.heading),
    latitude: parseNumber(vehicle.x),
    longitude: parseNumber(vehicle.y),
    alt: vehicle.alt !== undefined ? parseNumber(vehicle.alt) : undefined,
    sats: vehicle.sats !== undefined ? parseNumber(vehicle.sats) : undefined,
    lastUpdate: vehicle.last_update ?? "",
    driver: undefined,
    driverId: driverRef?.kind === "driver" ? driverRef.driverId : undefined,
    driverCode: driverRef?.kind === "code" ? driverRef.code : undefined,
    fuelLevel: 0,
    tankCapacity: parsePositiveNumber(vehicle.kapacitet_rezervoara),
    maxSpeed: parsePositiveNumber(vehicle.max_speed),
    trackSpeeding: parseFlag(vehicle.track_speeding),
    trackStreetSpeeding: parseFlag(vehicle.track_street_speeding),
    odometer: parseNumber(vehicle.odometer),
    ignition: vehicle.status === "1",
    isActive: vehicle.is_active !== "0",
    sensors: [],
    attrs: Array.isArray(vehicle.attrs) ? vehicle.attrs : undefined,
  };
}

/** Only keys explicitly present on `patch` are applied (undefined is skipped). */
export function pickDefinedRemoteVehicleFields(patch: Partial<RemoteVehicle>): Partial<RemoteVehicle> {
  const out: Partial<RemoteVehicle> = {};
  (Object.keys(patch) as (keyof RemoteVehicle)[]).forEach((key) => {
    const value = patch[key];
    if (value !== undefined) {
      (out as Record<string, unknown>)[key] = value;
    }
  });
  return out;
}

export function vehicleToRemote(vehicle: Vehicle): RemoteVehicle {
  return {
    id: vehicle.id,
    device_id: vehicle.deviceId,
    // Round-tripped like `driver_id`: a WS patch never carries it, and dropping it here would
    // lose the device type on the first telemetry merge — taking the live attr enrichment with it.
    device_type_id: vehicle.deviceTypeId,
    client_group_id: vehicle.groupId || undefined,
    maker: vehicle.maker,
    model: vehicle.model,
    registration: vehicle.plateNumber !== "-" ? vehicle.plateNumber : undefined,
    odometer: String(vehicle.odometer),
    year: vehicle.year != null ? String(vehicle.year) : undefined,
    kapacitet_rezervoara: vehicle.tankCapacity != null ? String(vehicle.tankCapacity) : undefined,
    max_speed: vehicle.maxSpeed != null ? String(vehicle.maxSpeed) : undefined,
    track_speeding: vehicle.trackSpeeding ? "1" : "0",
    track_street_speeding: vehicle.trackStreetSpeeding ? "1" : "0",
    last_update: vehicle.lastUpdate,
    x: String(vehicle.latitude),
    y: String(vehicle.longitude),
    speed: String(vehicle.speed),
    heading: String(vehicle.heading),
    alt: vehicle.alt !== undefined ? String(vehicle.alt) : undefined,
    sats: vehicle.sats !== undefined ? String(vehicle.sats) : undefined,
    status: vehicle.ignition ? "1" : "0",
    is_active: vehicle.isActive ? "1" : "0",
    // Round-tripped so a WS patch that omits it does not drop the assignment.
    driver_id: formatDriverRef(vehicleDriverRef(vehicle)) || undefined,
    attrs: vehicle.attrs,
  };
}

export function mergeVehicleWithRemotePatch(
  existing: Vehicle,
  patch: Partial<RemoteVehicle> & { id: string },
): Vehicle {
  const overlay = pickDefinedRemoteVehicleFields(patch);
  const base = vehicleToRemote(existing);
  const merged: RemoteVehicle = {
    ...base,
    ...overlay,
    id: patch.id,
  };
  const mapped = mapVehicle(merged);
  const withAttrs =
    patch.attrs === undefined && existing.attrs?.length
      ? { ...mapped, attrs: existing.attrs }
      : mapped;
  const withTelemetry = {
    ...withAttrs,
    alt: patch.alt !== undefined ? mapped.alt : existing.alt,
    sats: patch.sats !== undefined ? mapped.sats : existing.sats,
  };
  if (patch.client_group_id === undefined) {
    return { ...withTelemetry, groupName: existing.groupName };
  }
  return withTelemetry;
}

/** Build a vehicle from a remote patch only (e.g. first WS message for a new id). */
export function vehicleFromRemotePatch(patch: Partial<RemoteVehicle> & { id: string }): Vehicle {
  const overlay = pickDefinedRemoteVehicleFields(patch);
  return mapVehicle({
    id: patch.id,
    ...overlay,
  });
}

export async function listVehicles(): Promise<Vehicle[]> {
  const response = await apiRequest<ListResponse<RemoteVehicle>>("/api/vehicles", {
    method: "GET",
    cache: "no-store",
  });
  return response.data.map(mapVehicle);
}

/**
 * `odometer` is deliberately absent: `ClientDevice.php` accepts it, but total mileage is not the
 * client's to rewrite, so it is read-only in this app.
 */
type UpdateVehiclePayload = {
  client_group_id?: string;
  maker?: string;
  model?: string;
  registration?: string;
  year?: string;
  /** Tank size in litres. The empty string clears it upstream. */
  kapacitet_rezervoara?: string;
  /** Tagged driver reference — build it with `formatDriverRef`. The empty string clears it. */
  driver_id?: string;
  /** Speed limit in km/h. The empty string clears it upstream. */
  max_speed?: string;
  /** `"1"` / `"0"` — check speed against the vehicle's own max speed. */
  track_speeding?: string;
  /** `"1"` / `"0"` — check speed against the street speed limit. */
  track_street_speeding?: string;
};

/**
 * Resolves to `null` when upstream acknowledges the write without echoing the device back —
 * callers refetch the list rather than relying on the response.
 */
export async function updateVehicle(
  id: string,
  payload: UpdateVehiclePayload,
): Promise<Vehicle | null> {
  const response = await apiRequest<SingleResponse<RemoteVehicle>>(`/api/vehicles/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  const vehicle = response.data ? mapVehicle(response.data) : null;
  // The registration is what an admin reading the log recognises, and it is the one field upstream
  // reliably echoes back. Falls back to whatever the payload set it to when it does not.
  logCrud("update", "vehicle", vehicle?.plateNumber || payload.registration || "", id);
  return vehicle;
}

const BULK_UPDATE_CONCURRENCY = 4;

/** `ClientDevice.php` has no bulk endpoint; updates run 4-at-a-time and partial failure is reported. */
export async function updateVehicles(
  ids: string[],
  payload: UpdateVehiclePayload,
): Promise<{ updated: string[]; failed: { id: string; error: unknown }[] }> {
  const updated: string[] = [];
  const failed: { id: string; error: unknown }[] = [];

  for (let i = 0; i < ids.length; i += BULK_UPDATE_CONCURRENCY) {
    const batch = ids.slice(i, i + BULK_UPDATE_CONCURRENCY);
    const results = await Promise.allSettled(
      batch.map((id) => updateVehicle(id, payload)),
    );
    results.forEach((result, index) => {
      const id = batch[index];
      if (result.status === "fulfilled") {
        updated.push(id);
      } else {
        failed.push({ id, error: result.reason });
      }
    });
  }

  return { updated, failed };
}
